import 'engine';
import { browserTracingIntegration, captureMessage as SentryCaptureMessage, init as SentryInit, replayIntegration, setTag as sentrySetTag } from '@sentry/browser';
import { setLanguage, translations } from 'shared/translate';
import englishTranslations from './en.json';

const consoleError = console.error;
console.error = (msg, ...data) => {
	SentryCaptureMessage([msg, ...data].join(' '), 'error');
	consoleError(msg, ...data);
};

SentryInit({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	integrations: [browserTracingIntegration(), replayIntegration()],
	tracesSampleRate: 1.0,
	tracePropagationTargets: [],
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
});
import { app } from 'engine';
import { Dashboard } from 'pages/Dashboard';
import { Login } from 'pages/Login';
import { createRpc, getToken, rpc } from './backend';
import './themeSwitcher';
import { makeRouter, mountRouter, redirect } from 'engine/router';
import { DashboardOverview } from 'components/DashboardOverview';
import { Suspense } from 'components/Suspense';
import { UserView } from 'components/UserView';
import { UserList } from 'components/UserList';
import { UserEdit } from 'components/UserEdit';
import { UserCreate } from 'components/UserCreate';
import { showToast, Toast } from 'components/Toast';
import { FileLock } from 'lucide-static';
import { GameList } from 'components/GameList';
import { GameView } from 'components/GameView';
import type { GameId } from 'shared/modelTypes/index';
import { GameEdit } from 'components/GameEdit';
import { GameLicenseList } from 'components/GameLicenseList';
import { GameLicenseCreate } from 'components/GameLicenseCreate';
import { GameLicenseView } from 'components/GameLicenseView';
import { GameLicenseEdit } from 'components/GameLicenseEdit';
import { GamePlay } from 'components/GamePlay';
import { StatisticsView } from 'components/StatisticsView';

translations['en'] = englishTranslations;
setLanguage('en');

(async () => {
 await createRpc();

const appRouter = makeRouter();

const dashRouter = makeRouter();
dashRouter.template = <Dashboard></Dashboard>;
dashRouter.view = dashRouter.template!.querySelector('[page-container]')!;

appRouter.get('/', () => {
	if (getToken()) {
		// console.log(getToken());
		return redirect('/dashboard');
	} else {
		return redirect('/login');
	}
});
appRouter.get('/login', () => <Login />);
appRouter.get('/dashboard/*', dashRouter);
appRouter.get('/*', '404');

// --------------

// auth middleware
dashRouter.use((next) => {
	if (!getToken()) {
		showToast(<Toast icon={FileLock} title="Please sign in first." />, 3_000);
		return redirect('/login');
	}
	next();
});

dashRouter.get('/', () => <DashboardOverview />);

dashRouter.get('/users', () => <Suspense query={() => rpc.getUserList.query()} onLoaded={(data) => <UserList users={data} />} />);
dashRouter.get('/users/new', () => <UserCreate />);
dashRouter.get('/users/:id', ({ id }) => (
	<Suspense query={() => rpc.getUserById.query(id)} onLoaded={(user) => <UserView user={user!} />} />
));
dashRouter.get('/users/edit/:id', ({ id }) => (
	<Suspense query={() => rpc.getUserById.query(id)} onLoaded={(user) => <UserEdit user={user!} />} />
));

dashRouter.get('/games', () => <Suspense query={() => rpc.getGameList.query()} onLoaded={(data) => <GameList games={data} />} />);
dashRouter.get('/games/:kind', ({ kind }) => (
	<Suspense query={() => rpc.getGameById.query(kind as GameId)} onLoaded={(game) => <GameView game={game} />} />
));
dashRouter.get('/games/edit/:kind', ({ kind }) => (
	<Suspense query={() => rpc.getGameById.query(kind as GameId)} onLoaded={(game) => <GameEdit game={game} />} />
));

dashRouter.get('/licenses', () => (
	<Suspense
		query={() => rpc.getGameLicenseList.query()}
		onLoaded={(data) => <GameLicenseList licenses={data.licenses} role={data.role} users={data.users} />}
	/>
));
dashRouter.get('/licenses/new', () => (
	<Suspense query={() => rpc.getGameLicenceForCreate.query()} onLoaded={(data) => <GameLicenseCreate users={data.users} />} />
));
dashRouter.get('/licenses/:id', ({ id }) => {
	sentrySetTag('gameLicenseId', id);
	return (
		<Suspense
			query={() => rpc.getGameLicenceById.query(id)}
			onLoaded={(data) => <GameLicenseView game={data.game} license={data.license} role={data.role} />}
		/>
	);
});
dashRouter.get('/licenses/play/:id', ({ id }) => {
	sentrySetTag('gameLicenseId', id);
	return (
		<Suspense query={() => rpc.getGameLicenceById.query(id)} onLoaded={(data) => <GamePlay game={data.game} license={data.license} />} />
	);
});
dashRouter.get('/licenses/edit/:id', ({ id }) => {
	sentrySetTag('gameLicenseId', id);
	return (
		<Suspense
			query={async () => rpc.getGameLicenceForEditById.query(id)}
			onLoaded={(data) => <GameLicenseEdit license={data.license} users={data.users} />}
		/>
	);
});
dashRouter.get('/statistics', () => (
	<Suspense
		query={() => rpc.getUserList.query()}
		onLoaded={(users) => <StatisticsView users={users} />}
	/>
));

dashRouter.get('/*', '404');

mountRouter(app, appRouter);


})()

